import { useUser, useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "@/constants/api";
import { isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import {
  ArrowRight,
  CheckCircle2,
  X,
  BookOpen,
  Plus,
} from "lucide-react";
import { ReportFolderCard } from "@/components/entities/ReportFolderCard";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}


export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initError, setInitError] = useState(false);
  const [recentReportFolders, setRecentReportFolders] = useState([]);
  const [reportFoldersLoading, setReportFoldersLoading] = useState(true);
  useEffect(() => {
    const ac = new AbortController();
    const init = async () => {
      try {
        const token = await getToken();
        const folderRes = await fetch(`${BACKEND_URL}api/report-folders/recent?limit=6`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (folderRes.ok) {
          const recent = await folderRes.json();
          setRecentReportFolders(recent);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        setInitError(true);
      } finally {
        if (!ac.signal.aborted) setReportFoldersLoading(false);
      }
    };

    if (user) init();

    const query = new URLSearchParams(window.location.search);
    if (query.get("success")) {
      setPaymentSuccess(true);
      navigate("/dashboard", { replace: true });
    }
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const firstName =
    user?.firstName || user?.fullName?.split(" ")[0] || "Teacher";

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      {paymentSuccess && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-success-border bg-success-bg px-4 py-3 text-success-fg text-sm mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="shrink-0" />
            <span>Payment successful! Your plan has been upgraded.</span>
          </div>
          <button
            onClick={() => setPaymentSuccess(false)}
            className="shrink-0 opacity-70 hover:opacity-100"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {initError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive-border bg-destructive-bg px-4 py-3 text-destructive-fg text-sm mb-4">
          <div className="flex items-center gap-2">
            <X size={16} className="shrink-0" />
            <span>Could not connect to the server. Please refresh the page.</span>
          </div>
          <button
            onClick={() => setInitError(false)}
            className="shrink-0 opacity-70 hover:opacity-100"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <PageHeader
        title={`${getGreeting()}, ${firstName}.`}
        subtitle="Pick up where you left off."
      />

      {/* Recent Report Folders */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[0.8125rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-0">Recent Report Folders</h2>
          <Link to="/report-folders" className="inline-flex items-center gap-1 text-[0.8125rem] font-medium text-primary no-underline hover:opacity-75 transition-opacity">
            View all <ArrowRight size={12} />
          </Link>
        </div>

        {reportFoldersLoading ? (
          <div className="flex flex-wrap gap-[0.625rem]">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-[76px] w-[380px] rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : recentReportFolders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 bg-card border border-border rounded-xl text-center">
            <BookOpen size={26} className="text-muted-foreground opacity-50 mb-3" />
            <p className="text-[0.9375rem] font-semibold text-foreground m-0">No Report Folders yet</p>
            <p className="text-[0.8125rem] text-muted-foreground mt-1">
              Create your first Report Folder to start generating report cards.
            </p>
            <Link to="/report-folders">
              <Button size="sm" variant="outline" className="gap-2 mt-3">
                <Plus size={13} />
                New Report Folder
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap gap-[0.625rem]">
            {recentReportFolders.map((folder) => (
              <div key={folder._id} className="relative">
                <ReportFolderCard
                  item={folder}
                  getLinkPath={(item) =>
                    item.isShared
                      ? `/shared-report-folders/${item._id}`
                      : `/report-folders/${item._id}`
                  }
                />
                <span className="absolute top-2 right-2 z-20 pointer-events-none text-[10px] font-medium text-neutral-400 dark:text-white/40 bg-white/80 dark:bg-black/30 border border-neutral-200 dark:border-white/10 px-1.5 py-0.5 rounded-md backdrop-blur-sm">
                  {timeAgo(folder.updatedAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
